import { Group, Stack, TextInput } from "@mantine/core"
import { IconSearch } from "@tabler/icons-react"
import {
  PaginationState,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { Loader, Select } from "@tfg/ui"
import { Dispatch, SetStateAction, useState } from "react"
import { useTranslation } from "react-i18next"

import { FindProductLcasResponse } from "@tfg/types/schemas/modules/lifeCycleAnalysis/dtos/find-product-lcas.response.dto"
import { fetchDataOptionsValidationType } from "@tfg/types/schemas/products"

import { Table } from "~/components/table/Table.tsx"
import { RunLcaOnProductsButton } from "~/pages/Brand/Regulations/LCA/RunLcaOnProductsButton.tsx"
import { ECOBALYSE_ANALYSIS_STATUS_TRANSLATIONS } from "~/pages/Brand/Regulations/LCA/ecobalyseAnalysisStatusTranslations"
import { useLcaColumns } from "~/pages/Brand/Regulations/LCA/useLCAColumns.tsx"

interface Props {
  data: FindProductLcasResponse
  fetchDataOptions: fetchDataOptionsValidationType
  setPagination: Dispatch<SetStateAction<PaginationState>>
  isFetching: boolean
  searchProduct: string | undefined
  setSearchProduct: (
    value: ((prevState: string | undefined) => string) | string
  ) => void
  statusFilter: string | null
  setStatusFilter: Dispatch<SetStateAction<string | null>>
}

export const ProductsLCATable = ({
  data,
  fetchDataOptions,
  setPagination,
  isFetching,
  setSearchProduct,
  searchProduct,
  statusFilter,
  setStatusFilter,
}: Props) => {
  const [productIds, setProductIds] = useState<number[]>([])

  const columns = useLcaColumns(productIds, setProductIds, data.allProductIds)
  const { t } = useTranslation("regulations")

  const table = useReactTable({
    state: {
      pagination: fetchDataOptions,
    },
    manualPagination: true,
    data: data.data,
    pageCount: Math.ceil(data.total / fetchDataOptions.pageSize),
    columns,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  return (
    <Stack>
      <Group justify="space-between">
        <Group>
          <TextInput
            size="xs"
            w={180}
            autoFocus
            placeholder={t("lists.placeholders.search")}
            leftSection={<IconSearch size={16} />}
            value={searchProduct}
            onChange={(event) => setSearchProduct(event.currentTarget.value)}
            rightSection={isFetching && <Loader size={16} />}
          />
          <Select
            data={Object.entries(ECOBALYSE_ANALYSIS_STATUS_TRANSLATIONS).map(
              ([key, translation]) => ({
                value: key,
                label: translation,
              })
            )}
            value={statusFilter}
            clearable
            onChange={(value) => setStatusFilter(value)}
          />
        </Group>
        <Group>
          <RunLcaOnProductsButton
            params={{
              productIds,
            }}
          />
        </Group>
      </Group>
      <Table
        table={table}
        pagination={true}
        manualPaginationCount={data.total}
        pageIndex={fetchDataOptions.pageIndex}
      />
    </Stack>
  )
}
